import { isAxiosError } from "axios";
import type { UploadAdapter } from "@sdt/render-form-engine";
import {
  cancelUpload,
  directUpload,
  GcsUploadError,
  resumeDirectUpload,
} from "@/queries/uploads";
import type { DirectUploadOptions } from "@/queries/uploads";
import {
  attachUploadsToField,
  deleteFieldFile,
} from "@/queries/formality-request/intern-forms.api";
import { markUserActivity } from "@/utils/user-activity";
import { findPendingUploads, releaseBlockingUploads } from "./pending-uploads";

export const UPLOAD_ERROR_MESSAGES = {
  upload: "No se pudo subir el archivo.",
  attach: "No se pudo adjuntar el archivo.",
  connection:
    "No se pudo subir el archivo. Revise su conexión e inténtelo de nuevo.",
  sessionExpired:
    "Su sesión venció. Inicie sesión de nuevo y reintente la subida.",
  notFound: "No se encontró la subida de este archivo. Inténtelo de nuevo.",
  delete: "No se pudo eliminar el archivo.",
  deleteLocked: "El formulario ya no permite eliminar archivos.",
} as const;

// Las llamadas del flujo de subida no redirigen al login con un 401: el ítem
// queda en error y se avisa una sola vez (`onSessionExpired`).
const REQUEST_METADATA = { skip401Redirect: true };

const isAbortError = (err: unknown) =>
  err instanceof DOMException && err.name === "AbortError";

const getValidationErrors = (data: unknown): Record<string, unknown> => {
  const errors = (data as { errors?: unknown } | null)?.errors;
  return errors && typeof errors === "object"
    ? (errors as Record<string, unknown>)
    : {};
};

/** Primer mensaje de validación del backend (viene traducido). */
const getFirstValidationMessage = (
  errors: Record<string, unknown>,
): string | undefined => {
  for (const value of Object.values(errors)) {
    const message = Array.isArray(value) ? value[0] : value;
    if (typeof message === "string" && message.trim()) return message;
  }
  return undefined;
};

/** Traduce un fallo del flujo de subida al mensaje que muestra el ítem. */
export const getUploadErrorMessage = (
  err: unknown,
  fallback: string,
): string => {
  if (err instanceof GcsUploadError) return UPLOAD_ERROR_MESSAGES.connection;
  if (!isAxiosError(err)) return fallback;
  if (!err.response) return UPLOAD_ERROR_MESSAGES.connection;

  switch (err.response.status) {
    case 401:
      return UPLOAD_ERROR_MESSAGES.sessionExpired;
    case 404:
      return UPLOAD_ERROR_MESSAGES.notFound;
    case 422: {
      const errors = getValidationErrors(err.response.data);
      return getFirstValidationMessage(errors) ?? fallback;
    }
    default:
      return fallback;
  }
};

interface FormUploadAdapterOptions {
  /** Va como `context_id` al pedir la URL de subida. */
  assignmentId: string;
  /** Se llama una sola vez, con el primer 401 del flujo de subida. */
  onSessionExpired: () => void;
}

/**
 * Adaptador real del `UploadProvider` del motor sobre el contrato de subidas
 * directas a GCS (`/admin/v1/uploads`). El motor administra la cola, los
 * reintentos y la cancelación; aquí sólo se mueven bytes y se llama al backend.
 */
export const createFormUploadAdapter = ({
  assignmentId,
  onSessionExpired,
}: FormUploadAdapterOptions): UploadAdapter => {
  let sessionExpiredNotified = false;

  // El motor muestra `error.message` en el ítem y trata un AbortError como
  // una cancelación, no como un fallo, así que ése pasa tal cual.
  // `byStatus` reemplaza el mensaje de un código HTTP concreto.
  const run = async <T>(
    task: () => Promise<T>,
    fallback: string,
    byStatus: Partial<Record<number, string>> = {},
  ) => {
    try {
      return await task();
    } catch (err) {
      if (isAbortError(err)) throw err;
      if (
        isAxiosError(err) &&
        err.response?.status === 401 &&
        !sessionExpiredNotified
      ) {
        sessionExpiredNotified = true;
        onSessionExpired();
      }
      const status = isAxiosError(err) ? err.response?.status : undefined;
      throw new Error(
        (status && byStatus[status]) || getUploadErrorMessage(err, fallback),
        { cause: err },
      );
    }
  };

  return {
    uploadFile: (
      file,
      { signal, formFieldKey, onUploadCreated, onProgress, resume },
    ) =>
      run(async () => {
        const options: DirectUploadOptions = {
          context: "formality_request",
          context_id: assignmentId || undefined,
          form_field_key: formFieldKey,
          signal,
          onUploadCreated,
          onProgress,
          // El motor decide cuándo reintentar o cancelar: borrar el registro
          // al fallar impediría retomar la sesión.
          cancelOnFailure: false,
          // El adjunto confirma la subida; confirmar antes dejaría el archivo
          // en GCS sin campo si el adjunto falla (PPSS-4671, §3).
          skipConfirm: true,
          metadata: REQUEST_METADATA,
        };
        const result = resume
          ? await resumeDirectUpload(file, {
              ...options,
              id: resume.uploadId,
              sessionUri: resume.sessionUri,
            })
          : await directUpload(file, options);
        return { id: result.id };
      }, UPLOAD_ERROR_MESSAGES.upload),

    // Best-effort: un upload ya `COMPLETED` responde 422 y el motor ignora el fallo.
    cancelUpload: (uploadId) =>
      cancelUpload(uploadId, { metadata: REQUEST_METADATA }),

    attachUploadsToField: (targetAssignmentId, fieldId, uploadIds) =>
      run(
        () =>
          attachUploadsToField(targetAssignmentId, fieldId, uploadIds, {
            metadata: REQUEST_METADATA,
          }),
        UPLOAD_ERROR_MESSAGES.attach,
      ),

    // Fallar deja el archivo en el campo con el mensaje. Un 404 significa que
    // el archivo ya no está en el campo: eso es lo que se pedía.
    deleteFieldFile: (targetAssignmentId, fieldId, mediaUuid) =>
      run(
        async () => {
          try {
            await deleteFieldFile(targetAssignmentId, fieldId, mediaUuid, {
              metadata: REQUEST_METADATA,
            });
          } catch (err) {
            if (isAxiosError(err) && err.response?.status === 404) return;
            throw err;
          }
        },
        UPLOAD_ERROR_MESSAGES.delete,
        // 422: la asignación ya no admite cambios (enviada o en subsanación).
        { 422: UPLOAD_ERROR_MESSAGES.deleteLocked },
      ),

    // El token ya se refresca cada 30 s; lo que vence a mitad de una subida
    // larga es el temporizador de inactividad.
    keepSessionAlive: () => {
      markUserActivity();
      return Promise.resolve();
    },

    // Middleware de «Enviar»: el motor consulta las subidas pendientes de la
    // asignación (de cualquier pestaña o sesión) y, si hay, muestra su modal.
    getPendingUploads: async (targetAssignmentId) =>
      (await findPendingUploads(targetAssignmentId)) ?? [],

    cancelPendingUploads: releaseBlockingUploads,
  };
};
